// The client clock display (plan §6). Server clock snapshots are authoritative;
// between them the side to move counts down locally via `performance.now()`.
// Tenths are shown under 10s; the ticking side and low time are styled.

import type { ClockView, Color } from "./types.ts";

/** Below this (centis) a clock is "low" — styled urgently and warned about once. */
const LOW_CS = 1000; // 10s

export class ClockUI {
  private whiteCs = 0;
  private blackCs = 0;
  private turn: Color = "white";
  private running = false;
  private syncedAt = 0;
  private timer?: number;
  private lowFired: Record<Color, boolean> = { white: false, black: false };

  /** `onLow(color)` fires once when `color`'s running clock first dips under 10s
   *  (re-armed whenever a later sync puts it back above the threshold). */
  constructor(
    private els: Record<Color, HTMLElement>,
    private onLow?: (color: Color) => void,
  ) {}

  /** Adopt an authoritative snapshot and (re)start the local countdown. */
  sync(clock: ClockView | null, turn: Color, running: boolean): void {
    for (const el of Object.values(this.els)) el.hidden = clock === null;
    if (clock === null) { this.running = false; this.stopTicking(); return; }
    this.whiteCs = Math.round(clock.white * 100);
    this.blackCs = Math.round(clock.black * 100);
    this.turn = turn;
    this.running = running;
    this.syncedAt = performance.now();
    // Re-arm the low-time warning for any side now comfortably above the line
    // (e.g. after an increment lifted it back over 10s).
    for (const c of ["white", "black"] as Color[]) {
      if (this.remaining(c) > LOW_CS) this.lowFired[c] = false;
    }
    this.render();
    if (running) this.startTicking();
    else this.stopTicking();
  }

  /** Freeze the clocks (game over). */
  stop(): void {
    this.running = false;
    this.stopTicking();
    this.render();
  }

  destroy(): void {
    this.stopTicking();
  }

  private remaining(color: Color): number {
    let cs = color === "white" ? this.whiteCs : this.blackCs;
    if (this.running && this.turn === color) {
      cs -= Math.round((performance.now() - this.syncedAt) / 10);
    }
    return Math.max(0, cs);
  }

  private render(): void {
    for (const color of ["white", "black"] as Color[]) {
      const cs = this.remaining(color);
      const el = this.els[color];
      el.textContent = format(cs);
      const active = this.running && this.turn === color;
      el.classList.toggle("active", active);
      el.classList.toggle("low", cs <= LOW_CS); // under 10s
      // Fire the one-shot low-time warning for the side actually burning time.
      if (active && cs <= LOW_CS && !this.lowFired[color]) {
        this.lowFired[color] = true;
        this.onLow?.(color);
      }
    }
  }

  private startTicking(): void {
    this.stopTicking();
    this.timer = window.setInterval(() => this.render(), 100);
  }
  private stopTicking(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
  }
}

/** `M:SS` normally; `S.d` (seconds.tenths) under ten seconds. */
function format(cs: number): string {
  const secs = cs / 100;
  if (secs < 10) return secs.toFixed(1);
  const total = Math.floor(secs);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
